import { ItemBar } from "components/ItemBar";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { useContext, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useUpdateAccountMutation,
  useUpsertBenefitSelectionRulesMutation,
  useUpsertPayerAccountFeesMutation,
} from "store/queries/account";

import { validaTeCustomPaymentPlanSettings } from "../helpers/helpers";
import { mapFormToRequestBody } from "../helpers/mapFormToRequestBody";
import { rulesInSpecificityOrder } from "../helpers/rulesInSpecificityOrder";
import { wasFieldEdited } from "../helpers/wasFieldEdited";
import { toBenefitSelectionRulePayload } from "./BenefitSelection/mapper";
import { useUnsavedBenefitConfig } from "./BenefitSelection/UnsavedChanges";

export function SubmitEdit({
  id,
  initialAccountLocations = [],
}: {
  id: string;
  initialAccountLocations?: Array<{ id: string; name?: string }>;
}) {
  const [useUpdateAccount] = useUpdateAccountMutation();
  const [upsertPayerAccountFees] = useUpsertPayerAccountFeesMutation();
  const [upsertBenefitSelectionRules] =
    useUpsertBenefitSelectionRulesMutation();
  const { accountRefetch } = useUserWithAccount();
  const [buttonLoading, setButtonLoading] = useState(false);
  const { current: toastElement } = useContext(ToastContext);
  const {
    getValues,
    formState: { isValid, dirtyFields },
    watch,
  } = useFormContext();
  /*
    CLE-2792 — read during render on purpose. `formState` is a proxy that subscribes only to the
    parts a component touches while rendering, so reading `dirtyFields` inside the click handler
    would leave this component un-subscribed and the flag frozen at its mount value.
  */
  const benefitSelectionRulesEdited = wasFieldEdited(
    dirtyFields.benefitSelectionRules
  );
  const paymentPlanCustomSettingsField = watch("paymentPlanCustomSettings");
  const navigate = useNavigate();
  const unsavedBenefitConfig = useUnsavedBenefitConfig();

  const updateHospital = async () => {
    const formValues = getValues();
    const requestBody = await mapFormToRequestBody(formValues, {
      initialAccountLocations,
    });
    setButtonLoading(true);

    try {
      // Update the main account
      await useUpdateAccount({ ...requestBody, id }).unwrap();

      // We always update payer account fees (even if empty array to clear existing fees)
      const formattedPayerAccountFees = (formValues.payerAccountFees || []).map(
        (fee: any) => ({
          payerId: parseInt(fee.payerId, 10),
          type: fee.type || "ClearPatientFee",
          valueInPercentage: parseFloat(fee.valueInPercentage) || 0,
        })
      );

      await upsertPayerAccountFees({
        accountId: id,
        payerAccountFees: formattedPayerAccountFees,
      }).unwrap();

      /*
        CLE-2683 — sent whenever the payer-filters table on this form was edited, including when it
        was edited down to nothing, so removing the last rule genuinely clears them.

        AND NOT SENT AT ALL WHEN NOBODY TOUCHED IT. An untouched table has nothing to write, and
        re-sending it would still delete and recreate every row and log both halves to EventLog.
        (Until 2026-09-28 this also stopped Update Hospital overwriting filters saved from the
        benefit selection card; the card now edits the engine's own table, so that no longer
        applies. These rows are legacy's only.)

        ⚠️ `dirtyFields.benefitSelectionRules` is a per-field array, TRUTHY EVEN WHEN NOTHING
        CHANGED — see `wasFieldEdited`, which is what makes this an edit check rather than a
        has-been-written-through check.

        🚨 SORTED BY TODAY'S EFFECTIVE PRECEDENCE, NOT BY DISPLAY ORDER. The API now writes
        `priority = index`, so saving this list in the order it happens to be displayed would
        re-rank every account whose rules are not already in specificity order — see
        `rulesInSpecificityOrder`.
      */
      if (benefitSelectionRulesEdited) {
        await upsertBenefitSelectionRules({
          accountId: id,
          rules: rulesInSpecificityOrder(
            formValues.benefitSelectionRules || []
          ).map(toBenefitSelectionRulePayload),
        }).unwrap();
      }

      toastElement?.show({
        severity: "success",
        summary: "Account updated",
        detail: "Account updated successfully",
      });
      setButtonLoading(false);
      navigate("/hospitals");
      accountRefetch();
    } catch (error: any) {
      console.error(error);
      setButtonLoading(false);
      const message =
        error?.data?.message ||
        error?.message ||
        error?.data?.messages?.join(", ") ||
        error?.data?.messages?.[0] ||
        "An error has occurred attempting to update the account";
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: message,
      });
    }
  };

  /*
    "Update Hospital" does NOT save the benefit engine configuration — that card has its own Save —
    and it navigates away on success, which would drop the card's unsaved edits without a word.
    Somebody who sees "unsaved changes" and clicks the nearest save-looking button is exactly who
    this catches, so ask first. Closing the dialog (X / Escape) does neither.
  */
  const handleUpdateClick = () => {
    if (!unsavedBenefitConfig) {
      updateHospital();
      return;
    }
    const { canSave, blockedReason, save } = unsavedBenefitConfig;
    confirmDialog({
      header: "Unsaved benefit engine configuration",
      message: canSave
        ? "Update Hospital does not save the benefit engine configuration, and leaving this page loses your unsaved changes to it."
        : `Update Hospital does not save the benefit engine configuration, and it cannot be saved yet: ${blockedReason} Updating now loses those changes.`,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: canSave ? "Save both" : "Go back",
      rejectLabel: "Update without them",
      accept: async () => {
        if (!canSave) return;
        // Configuration first: if it fails, stay on the page with the edits intact.
        if (await save()) updateHospital();
      },
      reject: () => {
        updateHospital();
      },
    });
  };

  const submitButtonDIsabled = useMemo(() => {
    if (!isValid) return true;
    if (paymentPlanCustomSettingsField?.length) {
      const isCustomSettingsValid = validaTeCustomPaymentPlanSettings({
        settings: paymentPlanCustomSettingsField,
      });
      if (!isCustomSettingsValid) return true;
    }
    return false;
  }, [isValid, paymentPlanCustomSettingsField]);

  return (
    <ItemBar>
      <div
        data-testid="submit__Hospital"
        className="flex flex-1 justify-content-end"
      >
        <Button
          label="Update Hospital"
          onClick={handleUpdateClick}
          disabled={submitButtonDIsabled}
          loading={buttonLoading}
        />
      </div>
    </ItemBar>
  );
}
